import { useEffect, useRef } from 'react'
import { ENV } from '../lib/env'

/**
 * Cloudflare Turnstile widget for the auth forms. Managed mode is usually quick and quiet, but on a slow
 * connection it takes a moment; callers use useCaptcha().waitForToken() so they never submit before the token
 * is ready. onToken fires with the token on success and '' on expiry/error. It never hard-blocks: with no
 * site key configured, or if the script fails, it renders nothing. onReset hands the parent a function to
 * fetch a fresh token after a failed attempt (the token is single-use).
 */
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js'

interface TurnstileApi {
  render: (el: HTMLElement, opts: { sitekey: string; appearance?: string; callback: (t: string) => void; 'error-callback'?: () => void; 'expired-callback'?: () => void; 'timeout-callback'?: () => void }) => string
  remove: (id: string) => void
  reset: (id: string) => void
}
declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

let scriptPromise: Promise<boolean> | null = null
function loadScript(): Promise<boolean> {
  if (window.turnstile) return Promise.resolve(true)
  scriptPromise ??= new Promise<boolean>((resolve) => {
    const s = document.createElement('script')
    s.src = SCRIPT_SRC
    s.async = true
    s.defer = true
    s.onload = () => resolve(true)
    s.onerror = () => resolve(false)
    document.head.appendChild(s)
  })
  return scriptPromise
}

export function Turnstile({ onToken, onReset, labelled = false, appearance = 'always', className = '' }: { onToken: (token: string) => void; onReset?: (reset: () => void) => void; labelled?: boolean; appearance?: 'always' | 'interaction-only'; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const cb = useRef(onToken)
  cb.current = onToken

  useEffect(() => {
    if (!ENV.TURNSTILE_SITE_KEY) return
    let widgetId: string | undefined
    let cancelled = false
    void loadScript().then((ok) => {
      if (!ok || cancelled || !ref.current || !window.turnstile) return
      widgetId = window.turnstile.render(ref.current, {
        sitekey: ENV.TURNSTILE_SITE_KEY,
        // 'interaction-only' keeps the widget INVISIBLE while it self-solves (no "Success!" box), surfacing
        // only if Cloudflare actually needs a human action. Used where a token is required but a visible
        // check would be clutter (e.g. the signed-in confirm-email banner).
        appearance,
        callback: (t) => cb.current(t),
        'error-callback': () => cb.current(''),
        'expired-callback': () => cb.current(''),
        'timeout-callback': () => cb.current(''),
      })
      onReset?.(() => {
        try {
          if (widgetId && window.turnstile) window.turnstile.reset(widgetId)
        } catch {
          /* ignore */
        }
      })
    })
    return () => {
      cancelled = true
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId)
        } catch {
          /* already gone */
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!ENV.TURNSTILE_SITE_KEY) return null
  const invisible = appearance === 'interaction-only'
  return (
    <div className={className}>
      {labelled && !invisible && <p className="label-mono mb-1 text-[color:rgb(var(--muted))]">Security check</p>}
      {/* Reserve height so a visible widget does not shift the form; an invisible one takes no space. */}
      <div ref={ref} style={{ minHeight: invisible ? 0 : 65 }} />
    </div>
  )
}
